import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { checkCode, lookupCode, clientKeyOf } from '../discountStore';
import { cleanCode } from '../data/codes';
import { codeFromUrl, rememberCode, rememberedCode, forgetCode } from './codeCapture';

/**
 * A discount code on the booking page: one per visit, shared by the banner
 * above the services, the tags on covered services and the confirm sheet.
 *
 *   const code = useBookingCode({ enabled, salonId, isSelfEmployed })
 *
 *   code.typed              the code in play ('AUTUMN20'), or null
 *   code.info               what it is: { found, code, state } (for the banner)
 *   code.result             the check against the basket (quote()), or null
 *   code.apply(text, ctx)   check and keep a typed code
 *   code.check(ctx)         re-check the code in play against a new basket
 *   code.remove()           take it off
 *   code.linesFor(services, stylistId, priceFor)  the basket, as checkCode wants it
 *
 * The address bar is read on the very first render, before the page's deep
 * link handlers clear it.
 */
const SCOPE_REASONS = ['no_lines', 'scope', 'stylist'];

// scopeMessage: the "doesn't cover" wording for a basket that isn't services
// (the online shop: "That code doesn’t cover these products.").
export function useBookingCode({ enabled, salonId, isSelfEmployed, scopeMessage }) {
  const [fromUrl] = useState(() => (enabled ? codeFromUrl() : null));
  const [typed, setTyped] = useState(null);
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const alive = useRef(true);
  const seq = useRef(0);
  useEffect(() => () => { alive.current = false; }, []);

  // Once the salon is known: the link's code (remembered), or one from an
  // earlier visit in the last 14 days
  useEffect(() => {
    if (!enabled || !salonId) return;
    const c = fromUrl || rememberedCode(salonId);
    if (!c) return;
    if (fromUrl) rememberCode(salonId, fromUrl);
    setTyped(c);
    lookupCode({ salonId, typed: c }).then((r) => { if (alive.current) setInfo(r); }).catch(() => {});
  }, [enabled, salonId]); // eslint-disable-line react-hooks/exhaustive-deps

  const linesFor = useCallback((services, stylistId, priceFor) => (services || []).map((s, i) => ({
    id: `s${i}`,
    kind: 'service',
    serviceId: String(s.commonServiceId || s.id),
    categoryId: s.categoryId ? String(s.categoryId) : null,
    stylistId: stylistId ? String(stylistId) : null,
    price: typeof priceFor === 'function' ? Number(priceFor(s)) || 0 : Number(s.price) || 0,
    qty: 1,
  })), []);

  const selfEmployedIdsFor = (lines) => [...new Set(lines.map((l) => l.stylistId).filter((id) => id && isSelfEmployed && isSelfEmployed(id)))];

  const run = useCallback(async (text, ctx = {}) => {
    const mine = ++seq.current;
    setChecking(true);
    const lines = ctx.lines || [];
    let r = await checkCode({
      salonId,
      typed: text,
      lines,
      date: ctx.date ? moment(ctx.date).format('YYYY-MM-DD') : undefined,
      channel: 'online',
      client: { key: clientKeyOf(ctx.client || {}), isNew: ctx.client && ctx.client.isNew },
      selfEmployedIds: selfEmployedIdsFor(lines),
    }).catch(() => ({ ok: false, reason: 'error', message: 'Codes can’t be checked just now. Try again in a moment.', amount: 0, lines: [] }));
    if (scopeMessage && r && !r.ok && SCOPE_REASONS.includes(r.reason)) r = { ...r, message: scopeMessage };
    if (!alive.current || mine !== seq.current) return r;
    setChecking(false);
    setResult(r);
    if (r && r.code) setInfo({ found: true, code: r.code, state: r.ok ? 'live' : (info && info.state) || null, source: r.source });
    return r;
  }, [salonId, info]); // eslint-disable-line react-hooks/exhaustive-deps

  // Typed in the confirm sheet: kept (and remembered) when it works
  const apply = useCallback(async (text, ctx) => {
    const c = cleanCode(text);
    setError('');
    const r = await run(text, ctx);
    if (!alive.current) return r;
    if (r && r.ok) {
      setTyped(c);
      setDismissed(false);
      rememberCode(salonId, c);
    } else {
      setError((r && r.message) || 'That code isn’t right. Check it and try again.');
    }
    return r;
  }, [run, salonId]);

  const check = useCallback((ctx) => (typed ? run(typed, ctx) : Promise.resolve(null)), [run, typed]);

  const remove = useCallback(() => {
    seq.current += 1;
    setTyped(null);
    setResult(null);
    setInfo(null);
    setError('');
    setChecking(false);
    if (salonId) forgetCode(salonId);
  }, [salonId]);

  return {
    enabled: !!enabled,
    salonId,
    typed,
    info,
    result,
    checking,
    error,
    setError,
    dismissed,
    dismiss: () => setDismissed(true),
    apply,
    check,
    remove,
    linesFor,
  };
}

export default useBookingCode;
